import React, { useEffect, useState } from 'react';
import { useStripe, useElements } from '@stripe/react-stripe-js';
import { Box, useToast } from '@chakra-ui/react';
import { useSubscription } from '../../context/SubscriptionContext';

const PaymentRequestButton = ({ 
  amount, 
  currency = 'usd', 
  country = 'US',
  label,
  couponCode,
  onPaymentSuccess,
  onPaymentError 
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const { createPaymentRequestIntent } = useSubscription();
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [canMakePayment, setCanMakePayment] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (!stripe || !elements) {
      return;
    }

    const pr = stripe.paymentRequest({
      country,
      currency,
      total: {
        label,
        amount: amount, // Amount should be in cents
      },
      requestPayerName: true,
      requestPayerEmail: true,
    });

    // Check if Payment Request is available
    pr.canMakePayment().then(result => {
      if (result) {
        setPaymentRequest(pr);
        setCanMakePayment(true);
      }
    });

    // Handle payment method event
    pr.on('paymentmethod', async (ev) => {
      try {
        // Create PaymentIntent on the server
        const response = await createPaymentRequestIntent(
          amount,
          currency,
          label,
          couponCode
        );

        const { client_secret } = response;

        // Confirm the PaymentIntent without handling subsequent actions
        const { paymentIntent, error: confirmError } = await stripe.confirmCardPayment(
          client_secret,
          { payment_method: ev.paymentMethod.id },
          { handleActions: false }
        );

        if (confirmError) {
          ev.complete('fail');
          onPaymentError?.(confirmError);
          toast({
            title: 'Payment failed',
            description: confirmError.message,
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        } else {
          ev.complete('success');
          
          // Check if further action is required
          if (paymentIntent.status === 'requires_action') {
            // Let Stripe.js handle the action
            const { paymentIntent: finalPaymentIntent, error: actionError } = 
              await stripe.confirmCardPayment(client_secret);
            
            if (actionError) {
              onPaymentError?.(actionError);
              toast({
                title: 'Payment failed',
                description: actionError.message,
                status: 'error',
                duration: 5000,
                isClosable: true,
              });
            } else if (finalPaymentIntent.status === 'succeeded') {
              onPaymentSuccess?.(finalPaymentIntent, response.breakdown);
              toast({
                title: 'Payment successful!',
                status: 'success',
                duration: 3000,
                isClosable: true,
              });
            }
          } else if (paymentIntent.status === 'succeeded') {
            onPaymentSuccess?.(paymentIntent, response.breakdown);
            toast({
              title: 'Payment successful!',
              status: 'success',
              duration: 3000,
              isClosable: true,
            });
          }
        }
      } catch (error) {
        ev.complete('fail');
        onPaymentError?.(error);
        toast({
          title: 'Payment failed',
          description: error.message || 'An unexpected error occurred',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    });

  }, [stripe, elements, amount, currency, country, label, couponCode, createPaymentRequestIntent, onPaymentSuccess, onPaymentError, toast]);

  useEffect(() => {
    if (paymentRequest) {
      const prButton = elements.create('paymentRequestButton', {
        paymentRequest,
        style: {
          paymentRequestButton: {
            theme: 'dark',
            height: '40px',
            type: 'default', // Can be 'default', 'book', 'buy', or 'donate'
          },
        },
      });

      // Check if the Payment Request Button can be mounted
      paymentRequest.canMakePayment().then((result) => {
        if (result) {
          prButton.mount('#payment-request-button');
        }
      });

      return () => {
        prButton.unmount();
      };
    }
  }, [paymentRequest, elements]);

  if (!canMakePayment) {
    return null; // Don't render anything if payment request is not available
  }

  return (
    <Box>
      <div id="payment-request-button" style={{ marginBottom: '16px' }}>
        {/* This div will be replaced by the Stripe Payment Request Button */}
      </div>
    </Box>
  );
};

export default PaymentRequestButton;